import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { usePostalCity, portalLookup, formatCp } from "@/lib/postal";
import { Loader2, Home, CheckCircle2, Info, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CIVIL = [["solteiro", "Solteiro(a)"], ["casado", "Casado(a)"], ["divorciado", "Divorciado(a)"], ["viuvo", "Viúvo(a)"]];
const REGIME = [["casado_adquiridos", "Comunhão de adquiridos"], ["casado_separacao", "Separação de bens"], ["casado_geral", "Comunhão geral"], ["casado", "Não sei"]];
const inp = "w-full rounded-xl border-2 border-slate-200 px-3.5 py-3 text-[16px] outline-none focus:border-slate-400";
const SITUATIONS_PERSON = [
  ["is_family_home", "É a casa onde vive com a sua família", "Se for casado(a), o cônjuge terá de dar o seu acordo."],
  ["is_heranca", "Um dos donos do imóvel já faleceu", "Vamos pedir a habilitação de herdeiros e a certidão de óbito."],
  ["has_minor", "Um dos donos é menor ou não pode decidir sozinho", "É preciso autorização do tribunal ou do Ministério Público."],
  ["has_procuracao", "Outra pessoa vai assinar por mim (procuração)", "Vamos precisar da procuração."],
  ["is_rented", "O imóvel está arrendado (tem inquilinos)", "Vamos pedir o contrato e avisar o inquilino, que pode ter preferência."],
];
const SITUATIONS_COMPANY = [
  ["has_procuracao", "Outra pessoa vai assinar pela empresa (procuração)", "Vamos precisar da procuração."],
  ["is_rented", "O imóvel está arrendado (tem inquilinos)", "Vamos pedir o contrato e avisar o inquilino, que pode ter preferência."],
  ["statutes_minutes", "Os estatutos exigem uma ata para vender o imóvel", "Vamos pedir a ata (deliberação) que autoriza a venda."],
];
const STEPS = ["Os seus dados", "Situações", "Documentos"];
const hdr = (session) => ({ headers: { "X-Portal-Session": session } });

function Chips({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-3.5 py-2.5 rounded-xl border-2 text-sm font-medium transition-colors ${value === k ? "bg-ec-green border-ec-green text-white" : "border-slate-200 text-slate-700 bg-white"}`}>
          {l}
        </button>
      ))}
    </div>
  );
}

const Q = ({ label, children }) => <div className="space-y-2"><div className="text-sm font-semibold text-slate-800">{label}</div>{children}</div>;

function Progress({ step }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="of-progress">
      {STEPS.map((s, i) => (
        <div key={s} data-testid={`of-progress-${i + 1}`} data-state={i < step ? "done" : i === step ? "current" : "todo"}>
          <div className={`h-1.5 rounded-full ${i <= step ? "bg-ec-green" : "bg-slate-200"}`} />
          <div className={`mt-1.5 text-xs ${i === step ? "text-ec-green font-semibold" : "text-slate-400"}`}>{i + 1}. {s}</div>
        </div>
      ))}
    </div>
  );
}

export function OwnerForm({ token, session, form, onDone }) {
  const [step, setStep] = useState(form.step1_done ? 1 : 0);
  const [local, setLocal] = useState(null);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);
  return (
    <div className="space-y-6" data-testid="owner-form">
      <Progress step={step} />
      {step === 0
        ? <StepData token={token} session={session} form={local ? { ...form, draft: local } : form.step1_done ? { ...form, draft: {} } : form} onNext={(d) => { setLocal(d); setStep(1); }} />
        : <StepSituations token={token} session={session} form={form} onBack={() => setStep(0)} onDone={onDone} />}
    </div>
  );
}

export const visibleSituations = (form) => (form.company ? SITUATIONS_COMPANY : SITUATIONS_PERSON).filter(([k]) => !(form.team_answered || []).includes(k));

export function StepData({ token, session, form, onNext }) {
  const d = form.draft || {};
  const init = d.civil_status ?? form.civil_status ?? "";
  const [civil, setCivil] = useState(init.startsWith("casado") ? "casado" : init === "uniao_facto" ? "" : init);
  const [regime, setRegime] = useState(init.startsWith("casado") ? init : "");
  const [union, setUnion] = useState(d.union ?? (form.de_facto_union === true ? "sim" : form.de_facto_union === false ? "nao" : ""));
  const [f, setF] = useState({ spouse_name: form.spouse_name, address: form.address, postal_code: form.postal_code, locality: form.locality,
    email: form.email, quality: form.quality && form.quality !== form.owner_label ? "outro" : "default", quality_other: form.quality || "", ...(d.f || {}) });
  const [emailOk, setEmailOk] = useState(d.emailOk ?? (form.email ? "sim" : "nao"));
  const [phoneOk, setPhoneOk] = useState(d.phoneOk ?? form.phone_ok ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const first = useRef(true);
  const set = (k, v) => setF((o) => ({ ...o, [k]: v }));
  usePostalCity(f.postal_code, f.locality, (v) => set("locality", v), portalLookup(token, session));
  const cs = civil === "casado" ? regime : civil;
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const t = setTimeout(() => {
      axios.put(`${API}/portal/${token}/owner-draft`, { data: { civil_status: cs, union, f, emailOk, phoneOk } }, hdr(session))
        .then(() => setSaved(true)).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [cs, union, f, emailOk, phoneOk, token, session]);
  const livesThere = () => {
    const m = (form.property_address || "").match(/(\d{4}-\d{3})\s*(.*)$/);
    setF((o) => ({ ...o, address: (form.property_address || "").replace(/,?\s*\d{4}-\d{3}.*$/, "").replace(/^[^,]*T\d,\s*/i, ""),
      postal_code: m ? m[1] : o.postal_code, locality: m ? m[2] : o.locality }));
  };
  const firm = form.company;
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!firm && !cs) { setErr(civil === "casado" ? "Escolha o regime de bens." : "Escolha o estado civil."); return; }
    if (!firm && civil !== "casado" && !union) { setErr("Indique se vive em união de facto."); return; }
    if (!firm && civil === "casado" && !(f.spouse_name || "").trim()) { setErr("Indique o nome do cônjuge."); return; }
    if ((f.address || "").trim().length < 3 || (f.locality || "").trim().length < 2) { setErr("Indique a morada e a localidade."); return; }
    if (!/^\s*\d{4}\s*-\s*\d{3}\s*$/.test(f.postal_code || "")) { setErr("Código postal no formato 0000-000."); return; }
    if (emailOk === "nao" && f.email && !/^\S+@\S+\.\S+$/.test(f.email)) { setErr("Verifique o email."); return; }
    if (!firm && f.quality === "outro" && !(f.quality_other || "").trim()) { setErr("Indique em que qualidade assina."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/${token}/owner-form`, {
        ...(firm ? {} : { civil_status: cs, spouse_name: civil === "casado" ? f.spouse_name : "", de_facto_union: civil === "casado" ? false : union === "sim" }),
        address: f.address, postal_code: f.postal_code, locality: f.locality,
        email: f.email, phone_ok: phoneOk, quality: !firm && f.quality === "outro" ? f.quality_other : null,
      }, hdr(session));
      onNext({ civil_status: cs, union, f, emailOk, phoneOk });
    } catch (ex) {
      const det = ex.response?.data?.detail;
      setErr(typeof det === "string" ? det : "Verifique os dados e tente de novo.");
    }
    finally { setBusy(false); }
  };
  const phone = (form.phone || "").replace(/\D/g, "").replace(/^351/, "");
  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="of-step-1">
      <div>
        <h2 className="font-heading text-xl text-ec-green">Os seus dados</h2>
        <p className="text-sm text-slate-500 mt-1">Para prepararmos o seu contrato. Guardamos à medida que escreve{saved && <span className="text-green-700" data-testid="of-saved"> · guardado</span>}.</p>
      </div>
      {form.note && <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3.5 py-2.5" data-testid="owner-form-note">{form.note}</div>}
      {firm && (
        <div className="rounded-xl border-2 border-ec-green/30 bg-ec-green/5 px-4 py-3" data-testid="of-company-signer">
          <div className="text-sm font-semibold text-ec-green">Assina como gerente de {firm.name}</div>
          {firm.representative && <div className="text-sm text-slate-600 mt-0.5" data-testid="of-company-rep">{firm.representative}</div>}
        </div>
      )}
      {!firm && <Q label="Estado civil"><Chips options={CIVIL} value={civil} onChange={setCivil} testid="of-civil" /></Q>}
      {!firm && civil && civil !== "casado" && (
        <Q label="Vive em união de facto?"><Chips options={[["sim", "Sim"], ["nao", "Não"]]} value={union} onChange={setUnion} testid="of-union" /></Q>
      )}
      {!firm && civil === "casado" && (
        <>
          <Q label="Regime de bens"><p className="text-xs text-slate-500 -mt-1">Se não sabe, escolha Não sei.</p><Chips options={REGIME} value={regime} onChange={setRegime} testid="of-regime" /></Q>
          <Q label="Nome completo do cônjuge"><input className={inp} value={f.spouse_name} onChange={(e) => set("spouse_name", e.target.value)} required data-testid="of-spouse" /></Q>
        </>
      )}
      <Q label={firm ? "Morada da sede da empresa" : "Morada de residência"}>
        {!firm && <button type="button" onClick={livesThere} className="inline-flex items-center gap-1.5 text-sm text-ec-green underline" data-testid="of-lives-there"><Home size={14} /> Moro no imóvel</button>}
        <input className={inp} value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Rua, número, andar" required data-testid="of-address" />
        <div className="grid grid-cols-[120px_1fr] gap-2">
          <input className={inp} value={f.postal_code} onChange={(e) => set("postal_code", formatCp(e.target.value))} placeholder="0000-000" inputMode="numeric" required data-testid="of-cp" />
          <input className={inp} value={f.locality} onChange={(e) => set("locality", e.target.value)} placeholder="Localidade" required data-testid="of-locality" />
        </div>
      </Q>
      {form.email && (
        <Q label={`O seu email é ${form.email}?`}>
          <Chips options={[["sim", "Sim"], ["nao", "Não, é outro"]]} value={emailOk} onChange={(v) => { setEmailOk(v); if (v === "sim") set("email", form.email); }} testid="of-email-ok" />
        </Q>
      )}
      {emailOk === "nao" && <Q label="Email"><input type="email" className={inp} value={f.email} onChange={(e) => set("email", e.target.value)} required data-testid="of-email" /></Q>}
      {phone && (
        <Q label={`O seu telemóvel é ${phone.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}?`}>
          <Chips options={[["sim", "Sim"], ["nao", "Não, é outro"]]} value={phoneOk ? "sim" : "nao"} onChange={(v) => setPhoneOk(v === "sim")} testid="of-phone" />
          {!phoneOk && <p className="text-xs text-slate-500">O seu consultor vai confirmar o número consigo.</p>}
        </Q>
      )}
      {!firm && (
        <Q label="Em que qualidade assina?">
          <Chips options={[["default", form.owner_label], ["outro", "Outro"]]} value={f.quality} onChange={(v) => set("quality", v)} testid="of-quality" />
          {f.quality === "outro" && <input className={inp} value={f.quality_other} onChange={(e) => set("quality_other", e.target.value)} placeholder="Ex.: procurador, herdeiro" required data-testid="of-quality-other" />}
        </Q>
      )}
      {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="of-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="of-submit"
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold bg-ec-green disabled:opacity-50">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Continuar
      </button>
    </form>
  );
}

export function StepSituations({ token, session, form, onBack, onDone, confirmLabel = "Confirmar e ver documentos" }) {
  const SITUATIONS = visibleSituations(form);
  const other = form.special_other;
  const base = (k) => !!(other?.answers?.[k]);
  const [sel, setSel] = useState(() => Object.fromEntries(SITUATIONS.map(([k]) => [k, base(k) || !!form.special?.[k]])));
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async (values) => {
    setBusy(true); setErr("");
    try { await axios.post(`${API}/portal/${token}/owner-special`, { special: values }, hdr(session)); onDone(); }
    catch (ex) { const det = ex.response?.data?.detail; setErr(typeof det === "string" ? det : "Não foi possível guardar."); setBusy(false); }
  };
  const none = () => send(Object.fromEntries(SITUATIONS.map(([k]) => [k, base(k)])));
  useEffect(() => { if (!SITUATIONS.length) { none(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const any = SITUATIONS.some(([k]) => sel[k]);
  return (
    <div className="space-y-5" data-testid="of-step-2">
      <div>
        <h2 className="font-heading text-xl text-ec-green">Alguma destas situações se aplica?</h2>
        <p className="text-sm text-slate-500 mt-1">{other ? `${other.name} já respondeu. Pode acrescentar o que faltar.` : "Escolha todas as que se aplicam."}</p>
      </div>
      <div className="space-y-2">
        {SITUATIONS.map(([k, label, hint]) => {
          const locked = base(k);
          return (
            <div key={k} className={`rounded-xl border-2 transition-colors ${sel[k] ? "border-ec-green bg-ec-green/5" : "border-slate-200 bg-white"}`}>
              <div className="flex items-center">
                <button type="button" disabled={locked} onClick={() => setSel((o) => ({ ...o, [k]: !o[k] }))} data-testid={`of-sit-${k}`} aria-pressed={sel[k]}
                  className="flex-1 flex items-center gap-3 text-left px-3.5 py-3 text-[15px] text-slate-800 disabled:cursor-default">
                  <span className={`grid place-items-center w-6 h-6 rounded-md border-2 shrink-0 ${sel[k] ? "bg-ec-green border-ec-green text-white" : "border-slate-300"}`}>{sel[k] && <Check size={14} />}</span>
                  <span>{label}{locked && <span className="block text-xs text-slate-500">indicado por {other.name}</span>}</span>
                </button>
                <button type="button" onClick={() => setInfo(info === k ? null : k)} aria-label="Mais informação" data-testid={`of-sit-info-${k}`}
                  className="w-11 h-11 grid place-items-center text-slate-400 hover:text-ec-green shrink-0"><Info size={18} /></button>
              </div>
              {info === k && <p className="px-3.5 pb-3 -mt-1 text-xs text-slate-500" data-testid={`of-sit-hint-${k}`}>{hint}</p>}
            </div>
          );
        })}
      </div>
      {err && <div className="text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="of-error">{err}</div>}
      {any
        ? <button type="button" onClick={() => send(sel)} disabled={busy} data-testid="of-sit-confirm"
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold bg-ec-green disabled:opacity-50">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {confirmLabel}
          </button>
        : <button type="button" onClick={none} disabled={busy} data-testid="of-sit-none"
            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl text-white text-base font-semibold bg-ec-green shadow-lg disabled:opacity-50">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} Nenhuma destas
          </button>}
      <button type="button" onClick={onBack} className="w-full text-sm text-slate-500 underline" data-testid="of-back">Voltar aos meus dados</button>
    </div>
  );
}
